import { detectDeliveryZone, hasDeliveryZones } from '@/lib/deliveryZones';

const STREET_ADDRESS_RX =
  /\b[a-záéíóúñ][\wáéíóúñ.-]{2,}\s+\d{1,5}[a-z]?\b/i;

/**
 * @typedef {object} OrderPreviewLine
 * @property {string} name
 * @property {number} qty
 * @property {number} unitPrice
 * @property {number} subtotal
 * @property {string[]} removed
 * @property {string[]} addOns
 * @property {string} [cartLineId]
 */

/**
 * @typedef {object} OrderPreviewDelivery
 * @property {number|null} fee
 * @property {'ok'|'missing_comuna'|'out_of_zone'|'missing_address'|'needs_map'|'not_applicable'|'loading'} status
 * @property {string|null} [zoneName]
 * @property {string|null} [hint]
 */

/**
 * @typedef {object} OrderPreviewPayment
 * @property {string|null} method
 * @property {string|null} label
 * @property {number|null} cashAmount
 * @property {number|null} change
 */

/**
 * @typedef {object} OrderPreview
 * @property {OrderPreviewLine[]} lines
 * @property {number} subtotal
 * @property {OrderPreviewDelivery} delivery
 * @property {number} tip
 * @property {number} total
 * @property {string|null} address
 * @property {OrderPreviewPayment} payment
 * @property {string[]} warnings
 */

function lineUnitPrice(line) {
  return (
    Number(line?.price) ||
    Number(line?.product?.final_price) ||
    Number(line?.product?.price) ||
    0
  );
}

function looksLikeStreetAddress(address) {
  return STREET_ADDRESS_RX.test(String(address || ''));
}

export function paymentMethodToPreviewLabel(method) {
  const m = String(method || '').toLowerCase();
  if (m === 'transferencia') return 'Transferencia';
  if (m === 'efectivo') return 'Efectivo';
  if (m === 'online_manual' || m === 'online_card' || m === 'debito' || m === 'débito') {
    return 'Débito / Tarjeta';
  }
  if (m === 'online_mp' || m === 'mercadopago') return 'MercadoPago';
  return null;
}

/**
 * @param {string|null|undefined} address
 * @param {object|null|undefined} business
 * @param {{ serviceType?: string|null }} [opts]
 * @returns {OrderPreviewDelivery}
 */
export function resolveDeliveryPreview(address, business, opts = {}) {
  const serviceType = opts.serviceType || (address ? 'delivery' : null);
  const deliveryEnabled = Array.isArray(business?.service_types)
    ? business.service_types.includes('delivery')
    : false;

  if (!deliveryEnabled || serviceType === 'retiro' || serviceType === 'mesa') {
    return { fee: null, status: 'not_applicable', zoneName: null, hint: null };
  }

  const addr = String(address || '').trim();
  if (!addr) {
    return { fee: null, status: 'missing_address', zoneName: null, hint: null };
  }

  if (hasDeliveryZones(business)) {
    const zone = detectDeliveryZone(addr, business.delivery_zones);
    if (zone) {
      const price = Number(zone.price);
      return {
        fee: Number.isFinite(price) ? price : 0,
        status: 'ok',
        zoneName: String(zone.name || '').trim() || null,
        hint: null,
      };
    }
    const zoneNames = (business.delivery_zones || [])
      .map((z) => String(z?.name || '').trim())
      .filter(Boolean);
    if (looksLikeStreetAddress(addr)) {
      return {
        fee: null,
        status: 'missing_comuna',
        zoneName: null,
        hint:
          zoneNames.length > 0
            ? `Agregá la comuna. Llegamos a: ${zoneNames.join(', ')}`
            : 'Falta comuna para calcular el delivery',
      };
    }
    return {
      fee: null,
      status: 'out_of_zone',
      zoneName: null,
      hint: zoneNames.length > 0 ? `Zonas con delivery: ${zoneNames.join(', ')}` : 'No llegamos a esa dirección',
    };
  }

  return {
    fee: null,
    status: 'needs_map',
    zoneName: null,
    hint: 'Confirmá la dirección en el mapa para ver el costo de envío',
  };
}

function deliveryWarning(delivery) {
  if (delivery.status === 'missing_comuna') return 'Falta comuna';
  if (delivery.status === 'out_of_zone') return 'Fuera de zona de delivery';
  if (delivery.status === 'needs_map') return 'Delivery por confirmar en mapa';
  if (delivery.status === 'missing_address') return 'Falta dirección de envío';
  return null;
}

export function resolveTipAmount(hints = {}, subtotal = 0, deliveryFee = 0) {
  const fixed = Number(hints.tip);
  if (Number.isFinite(fixed) && fixed > 0) return Math.round(fixed);
  const pct = Number(hints.tipPercent ?? hints.tip_percent);
  const base = Math.max(0, Number(subtotal) || 0) + Math.max(0, Number(deliveryFee) || 0);
  if (Number.isFinite(pct) && pct > 0 && pct <= 100 && base > 0) {
    return Math.round((base * pct) / 100);
  }
  return 0;
}

/**
 * @param {object[]} cart
 * @param {object|null|undefined} business
 * @param {{ address?: string|null, paymentMethod?: string|null, cashAmount?: number|null, serviceType?: string|null, tip?: number|null, tipPercent?: number|null, tip_percent?: number|null }} [hints]
 * @returns {OrderPreview}
 */
export function buildOrderPreview(cart, business, hints = {}) {
  const lines = (Array.isArray(cart) ? cart : []).map((line) => {
    const qty = Math.max(1, Number(line.quantity) || 1);
    const unitPrice = lineUnitPrice(line);
    const addOns = (line.addOns || [])
      .map((a) => {
        const name = String(a?.name || '').trim();
        if (!name) return null;
        const aq = Math.max(1, Number(a?.quantity) || 1);
        return aq > 1 ? `${name} ×${aq}` : name;
      })
      .filter(Boolean);
    return {
      name: line.product?.name || 'Producto',
      qty,
      unitPrice,
      subtotal: unitPrice * qty,
      removed: Array.isArray(line.removedIngredients)
        ? line.removedIngredients.filter(Boolean)
        : [],
      addOns,
      cartLineId: line.id != null ? String(line.id) : undefined,
    };
  });

  const subtotal = lines.reduce((sum, l) => sum + l.subtotal, 0);
  const address = hints.address != null ? String(hints.address).trim() || null : null;
  const delivery = resolveDeliveryPreview(address, business, {
    serviceType: hints.serviceType || (address ? 'delivery' : null),
  });
  const deliveryFee = delivery.status === 'ok' && typeof delivery.fee === 'number' ? delivery.fee : 0;
  const tip = resolveTipAmount(hints, subtotal, deliveryFee);
  const total = subtotal + deliveryFee + tip;

  const paymentMethod = hints.paymentMethod ? String(hints.paymentMethod) : null;
  const cashAmount =
    hints.cashAmount != null && Number.isFinite(Number(hints.cashAmount)) && Number(hints.cashAmount) > 0
      ? Math.round(Number(hints.cashAmount))
      : null;
  const change =
    cashAmount != null && paymentMethod === 'efectivo' && cashAmount > total ? cashAmount - total : null;

  const warnings = [];
  const dw = deliveryWarning(delivery);
  if (dw) warnings.push(dw);

  return {
    lines,
    subtotal,
    delivery,
    tip,
    total,
    address,
    payment: {
      method: paymentMethod,
      label: paymentMethodToPreviewLabel(paymentMethod),
      cashAmount,
      change,
    },
    warnings,
  };
}
